/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { keysToCamel, keysToSnake } from '@/utils/utils';
import { apolloClient } from '@/apollo-client';
import {
  SaveParams,
  FieldConfigParams,
  FieldConfig,
  PostBookmarkParams,
  DeleteBookmarkParams,
  SuccessResponse,
  PostParams,
  FetchJiraIssuesResponse,
  FetchJiraIssuesParams,
  UnlinkJiraPostParams,
  CreateNewJiraIssuePostParams,
  LinkJiraIssueRequest,
  CreateNewObjectResponse,
  watchObjectParams,
  CreateFetcherTaskParams,
  MentionsUserListParams,
  LayoutPostParams,
  SaveLayoutConfigParams,
  RemoveSectionParams,
  AvatarParams,
  CommentsListParams,
} from '@/components/cards/types';
import {
  SiteConfigResponseSchema,
  SiteConfigUserSchema,
  globalSettingSchema,
  FeatureFlagsSchema,
} from '@/types/apiTypes';
import { Alert } from '@/components/global_alerts/types';
import { JiraProject, JiraIssueType, JiraComponent } from '@/components/jira/types';
import { Fetcher, FetcherTask } from '@/components/fetchers/types';
import { ExternalRequestsSyncResponse } from '@/components/external_requests/types';
import { SamlSettings, AuthenticationSettings } from '@/pages/settings/authentication/types';
import { putRequest, getRequest, postRequest, deleteRequest, sendRequest, ZenError } from './client/client';

// Edit modals
export const updateObjectField = async ({
  value = null,
  ignoreWarnings,
  comment = '',
  field,
  id,
  type,
  csrf,
}: SaveParams) => {
  return new Promise((resolve, reject) => {
    putRequest({
      url: `/api/v2/crud/${type}/${id}/${field}`,
      data: keysToSnake({ value, ignoreWarnings, comment }),
      headers: { 'X-CSRF-Token': csrf },
    })
      .then(resolve)
      .catch((error: ZenError) => reject(error.response!.data));
  });
};

export const loadObjectFieldConfig = async ({ type, id, field }: FieldConfigParams): Promise<FieldConfig> => {
  const queryParams = id ? `?object_id=${id}` : '';
  return new Promise((resolve, reject) => {
    getRequest({ url: `/api/v2/attrs_config/${type}/${field}${queryParams}` })
      .then((responseData: any) => resolve(keysToCamel(responseData) as FieldConfig))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<FieldConfig>;
};

export const loadMentionUserList = async ({ csrf, type, id }: MentionsUserListParams): Promise<FieldConfig> => {
  return new Promise((resolve, reject) => {
    postRequest({
      url: '/api/v2/comments/user_list',
      data: { object_type: type, object_id: id },
      headers: { 'X-CSRF-Token': csrf },
    })
      .then((responseData: any) => resolve(keysToCamel(responseData) as FieldConfig))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<FieldConfig>;
};

export const loadCommentsList = async ({ csrf, type, id }: CommentsListParams): Promise<FieldConfig> => {
  return new Promise((resolve, reject) => {
    postRequest({
      url: '/api/v2/comments/list',
      data: { object_type: type, object_id: id },
      headers: { 'X-CSRF-Token': csrf },
    })
      .then((responseData: any) => resolve(keysToCamel(responseData) as FieldConfig))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<FieldConfig>;
};

// Bookmarks
const BOOKMARKS_URL = '/api/v2/bookmarks';
export const saveBookmark = async ({ csrf, id, link, title }: PostBookmarkParams): Promise<SuccessResponse> =>
  new Promise((resolve, reject) => {
    postRequest({
      url: BOOKMARKS_URL + (id !== null ? `/${id}` : ''),
      data: { title, link },
      headers: { 'X-CSRF-Token': csrf },
    })
      .then(responseData => resolve(keysToCamel(responseData) as SuccessResponse))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<SuccessResponse>;

export const removeBookmark = async ({ csrf, id }: DeleteBookmarkParams): Promise<Boolean> =>
  new Promise((resolve, reject) => {
    deleteRequest({ url: `${BOOKMARKS_URL}/${id}`, headers: { 'X-CSRF-Token': csrf } })
      .then(() => resolve(true))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<Boolean>;

// Custom attributes
export const saveCustomAttribute = async ({ csrf, url, data }: PostParams): Promise<object> =>
  new Promise((resolve, reject) => {
    postRequest({ url, data: keysToSnake(data), headers: { 'X-CSRF-Token': csrf } })
      .then(responseData => resolve(keysToCamel(responseData)))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<object>;

// JIRA integration
export const fetchJiraIssues = async ({ searchString }: FetchJiraIssuesParams): Promise<FetchJiraIssuesResponse> =>
  new Promise((resolve, reject) => {
    getRequest({ url: '/jira/query_issues/' + searchString })
      .then(responseData => resolve(keysToCamel(responseData) as FetchJiraIssuesResponse))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<FetchJiraIssuesResponse>;

export const linkJiraIssue = async ({ csrf, issueId, objType, objId }: LinkJiraIssueRequest): Promise<object> => {
  const data = { objType, objId, issueId };
  return new Promise((resolve, reject) =>
    postRequest({ url: '/jira/update_linked_issue', data: keysToSnake(data), headers: { 'X-CSRF-Token': csrf } })
      .then(responseData => resolve(keysToCamel(responseData)))
      .catch((error: ZenError) => reject(error.response!.data))
  ) as Promise<object>;
};

export const unlinkJiraIssue = async ({ csrf, postUrl }: UnlinkJiraPostParams): Promise<Boolean> =>
  new Promise((resolve, reject) => {
    postRequest({ url: postUrl, data: null, headers: { 'X-CSRF-Token': csrf } })
      .then(responseData => resolve(responseData))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<Boolean>;

const NEW_JIRA_ISSUE_URL = '/jira/create_linked_issue';
export const createNewJiraIssue = async ({
  csrf,
  fields,
  objectId,
  objectType,
}: CreateNewJiraIssuePostParams): Promise<Boolean> =>
  new Promise((resolve, reject) => {
    postRequest({
      url: NEW_JIRA_ISSUE_URL,
      data: { fields, obj_id: objectId, obj_type: objectType },
      headers: {
        'X-CSRF-Token': csrf,
        'Content-Type': 'application/json',
      },
    })
      .then(() => resolve(true))
      .catch((error: ZenError) => reject(error.response!.data.message));
  }) as Promise<Boolean>;

export const getJiraProjects = async (): Promise<JiraProject[]> =>
  new Promise((resolve, reject) => {
    getRequest({ url: '/jira/get_projects' })
      .then(responseData => resolve(responseData))
      .catch((error: ZenError) => reject(error.response!.data));
  });

export const getJiraProjectIssueTypes = async (projectId: string): Promise<JiraIssueType[]> =>
  new Promise((resolve, reject) => {
    getRequest({ url: `/jira/get_project?id=${projectId}` })
      .then(responseData => resolve(responseData.issueTypes))
      .catch((error: ZenError) => reject(error.response!.data));
  });

export const getProjectComponents = async (projectId: string): Promise<JiraComponent[]> =>
  new Promise((resolve, reject) => {
    getRequest({ url: `/jira/get_components?project_id=${projectId}` })
      .then(responseData => resolve(responseData))
      .catch((error: ZenError) => reject(error.response!.data));
  });

export const watchObject = async ({ csrf, postUrl, unwatch }: watchObjectParams): Promise<Boolean> =>
  new Promise((resolve, reject) => {
    sendRequest({
      method: unwatch ? 'delete' : 'post',
      url: postUrl,
      headers: { 'X-CSRF-Token': csrf },
    })
      .then(responseData => resolve(responseData))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<Boolean>;

// Surveys
export const resendSurvey = ({ url, data, csrf }: PostParams) => {
  return putRequest({
    url,
    data: JSON.stringify(keysToSnake(data)),
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => error.response!.data);
};

// Loads site config
const SITE_CONFIG_URL = `/api/v2/site_config`;

export const loadSiteConfig = (): Promise<SiteConfigResponseSchema> => {
  return new Promise((resolve, reject) => {
    getRequest({ url: SITE_CONFIG_URL, withCredentials: true })
      .then(data => {
        // Keep case for feature flags
        const featureFlags = data['feature_flags'];
        delete data['feature_flags'];

        const formatted = keysToCamel(data) as SiteConfigResponseSchema;
        formatted.featureFlags = featureFlags;

        // Get currentUser field from GraphQL API
        if (featureFlags.graphql_api) {
          const siteConfig = require('@/graphql/SiteConfig.gql');
          apolloClient
            .query({ query: siteConfig })
            .then(resp => {
              formatted.currentUser = resp.data.currentUser as SiteConfigUserSchema;
              formatted.globalSettings = resp.data.globalSettings as globalSettingSchema;
              // We need to convert GraphQL response to an object.
              let featureFlags: any = {};
              // TODO: no in-place object manipulation
              resp.data.featureFlags.edges.map(edge => {
                featureFlags[edge.node.key] = edge.node.state;
              });
              formatted.featureFlags = featureFlags as FeatureFlagsSchema;
              resolve(formatted);
            })
            .catch(error => {
              // In case GraphQL API gives an error, we fallback to REST API response
              // eslint-disable-next-line no-console
              console.error(`GraphQL API Error ${error}`);
              formatted.globalSettings = (formatted as unknown) as globalSettingSchema;
              resolve(formatted);
            });
        } else {
          resolve(formatted);
        }
      })
      .catch(reject);
  });
};

// System settings
export const saveSystemSettings = ({ url, data, csrf }: PostParams) =>
  postRequest({ url, data, headers: { 'X-CSRF-Token': csrf } });

// SAML Group Settings
export const loadSAMLSettings = (url: string): Promise<object> =>
  new Promise((resolve, reject) => {
    getRequest({ url })
      .then(responseData => resolve(keysToCamel(responseData)))
      .catch(reject);
  });

export const saveSAMLSettings = ({ url, data, csrf }: PostParams) => {
  return new Promise((resolve, reject) => {
    postRequest({
      url,
      data: JSON.stringify(keysToSnake(data)),
      headers: {
        'X-CSRF-Token': csrf,
        'Content-Type': 'application/json',
      },
    })
      .then(responseData => resolve(keysToCamel(responseData)))
      .catch(error => reject(error.response!.data));
  });
};

// Global Authentication Settings
export const loadAuthenticationSettings = (): Promise<AuthenticationSettings | object> =>
  getRequest({ url: '/api/v2/settings/authentication' })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const saveAuthenticationSettings = (
  data: AuthenticationSettings,
  csrf: string
): Promise<AuthenticationSettings | object> =>
  postRequest({
    url: '/api/v2/settings/authentication',
    data: JSON.stringify(keysToSnake(data)),
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

// SAML IDP/SP Settings
export const loadSamlIdpSettings = (): Promise<SamlSettings | object> =>
  getRequest({ url: '/api/v2/settings/saml/idp' })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const saveSamlIdpSettings = (data: SamlSettings, csrf: string): Promise<SamlSettings | object> =>
  postRequest({
    url: '/api/v2/settings/saml/idp',
    data: JSON.stringify(keysToSnake(data)),
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const refreshSamlSpSettings = (csrf: string): Promise<SamlSettings | object> =>
  postRequest({
    url: '/api/v2/settings/saml/sp/refresh',
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const createSamlSpCert = (csrf: string): Promise<SamlSettings | object> =>
  postRequest({
    url: '/api/v2/settings/saml/sp/cert',
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const deleteSamlSpCert = (datetime: string, csrf: string): Promise<SamlSettings | object> =>
  deleteRequest({
    url: `/api/v2/settings/saml/sp/cert/${encodeURIComponent(datetime)}`,
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

// Autocomplete for JIRA users
export const findJiraUsers = (
  projectId: string,
  issueTypeId: string,
  fieldKey: string,
  query: string
): Promise<object> => {
  return new Promise((resolve, reject) => {
    const encodedQuery = encodeURIComponent(query);
    getRequest({ url: `/jira/get_users/${projectId}/${issueTypeId}/${fieldKey}/${encodedQuery}` })
      .then(responseData => resolve(responseData))
      .catch(reject);
  });
};

// Load HTML for entire "new object" page
export const loadNewObjectPage = async (url: string): Promise<string> => {
  const data = await getRequest({ url });
  return data;
};

// Post new object form
export const createNewObject = ({ url, data, csrf }: PostParams): Promise<CreateNewObjectResponse | Alert> => {
  return new Promise((resolve, reject) => {
    postRequest({
      url,
      data: JSON.stringify(keysToSnake(data)),
      headers: {
        'X-CSRF-Token': csrf,
        'Content-Type': 'application/json',
      },
    })
      .then(responseData => {
        if (responseData.success) {
          resolve(keysToCamel(responseData) as CreateNewObjectResponse);
        } else {
          reject(keysToCamel(responseData) as Alert);
        }
      })
      .catch(error => {
        reject(keysToCamel(error.response!.data) as Alert);
      });
  });
};

// Fetchers
export const loadFetchers = async (objectType: string, objectId: string): Promise<Fetcher[] | string> => {
  const url =
    '/api/v2/fetchers' +
    `?object_type=${encodeURIComponent(objectType)}` +
    `&object_id=${encodeURIComponent(objectId)}`;

  const responseData = await getRequest({ url });
  return keysToCamel(responseData.fetchers) as Fetcher[];
};

export const loadFetcherTask = async (taskUrl: string) => {
  // const url = `/api/v2/fetchers/tasks/${taskId}`;

  return new Promise((resolve, reject) => {
    getRequest({ url: taskUrl })
      .then((responseData: object) => {
        resolve(keysToCamel(responseData) as FetcherTask);
      })
      .catch(error => {
        reject(error.response!.data);
      });
  });
};

export const createFetcherTask = ({
  csrfToken,
  objectType,
  objectId,
  fetcherId,
}: CreateFetcherTaskParams): Promise<FetcherTask | string> => {
  return new Promise((resolve, reject) => {
    postRequest({
      url: '/api/v2/fetchers/task',
      data: keysToSnake({ objectType, objectId, fetcherId }),
      headers: {
        'X-CSRFToken': csrfToken,
        'X-requested-by': 'gGRC',
      },
    })
      .then((responseData: object) => {
        resolve(keysToCamel(responseData) as FetcherTask);
      })
      .catch(error => {
        reject(error.response!.data);
      });
  });
};

// External requests
export const syncExternalRequest = (url: string, csrf: string): Promise<ExternalRequestsSyncResponse> => {
  return postRequest({
    url,
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  }).then((responseData: object) => {
    return keysToCamel(responseData) as ExternalRequestsSyncResponse;
  });
};

// Object review
export const updateObjectStatus = (url: string, csrf: string, status: string, reviewId: string) => {
  return putRequest({ url, data: keysToSnake({ status, reviewId }), headers: { 'X-CSRF-Token': csrf } });
};

// ServiceNow Settings
export const ticketingDisconnect = (csrf: string): Promise<SamlSettings | object> =>
  getRequest({
    url: '/api/v2/ticketing/settings/disconnect',
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const ticketingCheckConnection = (csrf: string): Promise<SamlSettings | object> =>
  getRequest({
    url: '/api/v2/ticketing/settings/check_connection',
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const ticketingConnectionInfo = (csrf: string): Promise<SamlSettings | object> =>
  getRequest({
    url: '/api/v2/ticketing/settings/connection_info',
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const ticketingSetup = (csrf: string, data: object): Promise<SamlSettings | object> =>
  postRequest({
    url: '/api/v2/ticketing/settings/setup',
    data,
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

export const ticketingGetAuthInfo = (csrf: string): Promise<SamlSettings | object> =>
  getRequest({
    url: '/api/v2/ticketing/settings/auth_info',
    headers: {
      'X-CSRF-Token': csrf,
      'Content-Type': 'application/json',
    },
  })
    .then(responseData => keysToCamel(responseData))
    .catch(error => Promise.reject(error.response!.data));

// Custom attributes layout
export const saveAttrLayout = async ({ csrf, url, data }: LayoutPostParams<SaveLayoutConfigParams>): Promise<object> =>
  new Promise((resolve, reject) => {
    postRequest({ url, data: keysToSnake(data), headers: { 'X-CSRF-Token': csrf } })
      .then(responseData => resolve(keysToCamel(responseData)))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<object>;

export const removeLayoutSections = async ({
  csrf,
  url,
  data,
}: LayoutPostParams<RemoveSectionParams>): Promise<object> =>
  new Promise((resolve, reject) => {
    postRequest({ url, data: keysToSnake(data), headers: { 'X-CSRF-Token': csrf } })
      .then(responseData => resolve(keysToCamel(responseData)))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<object>;

// Profile
export const loadUserProfile = async (): Promise<FieldConfig> => {
  return new Promise((resolve, reject) => {
    getRequest({ url: '/api/v2/profile' })
      .then(responseData => resolve(keysToCamel(responseData) as FieldConfig))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<FieldConfig>;
};

export const uploadAvatar = async ({ fileData, imageName, csrf }: AvatarParams) => {
  let formData = new FormData();
  formData.append('file', fileData);
  formData.append('fileName', imageName);
  return new Promise((resolve, reject) => {
    putRequest({ url: '/api/v2/profile', data: formData, headers: { 'X-CSRF-Token': csrf } })
      .then(responseData => resolve(keysToCamel(responseData) as FieldConfig))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<FieldConfig>;
};

export const deleteAvatar = async ({ csrf }: AvatarParams) => {
  return new Promise((resolve, reject) => {
    deleteRequest({ url: '/api/v2/profile', headers: { 'X-CSRF-Token': csrf } })
      .then(responseData => resolve(keysToCamel(responseData) as FieldConfig))
      .catch((error: ZenError) => reject(error.response!.data));
  }) as Promise<FieldConfig>;
};
